import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, Loader2, Sparkles, Zap } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { openCheckout } from "@/lib/razorpay";
import { toast } from "sonner";

/**
 * CreditPacksDialog — one-time credit top-ups when a user hits their cap.
 * Currently runs in DEMO MODE on the backend (no payment gateway).
 * When Razorpay is wired, the purchase call will redirect to a Razorpay
 * checkout instead of immediately marking the purchase 'paid'.
 */
export default function CreditPacksDialog({ open, onClose }) {
  const { refresh } = useAuth();
  const [packs, setPacks] = useState([]);
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    http
      .get("/credits/packs")
      .then(({ data }) => {
        if (!cancelled) setPacks(data.packs || []);
      })
      .catch(() => {
        if (!cancelled) toast.error("Could not load credit packs");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const onPurchase = async (pack) => {
    setBusy(pack.id);
    try {
      const { data } = await http.post("/credits/purchase", { pack_id: pack.id });

      // Demo path — no Razorpay configured, server immediately marked it paid
      if (data.demo) {
        toast.success(data.message || `+${pack.credits} credits added`);
        await refresh?.();
        onClose();
        return;
      }
      if (data.affiliate && data.affiliate.applied) {
        toast.success(
          `${data.affiliate.partner_name} discount applied · ${data.affiliate.discount_pct}% off ($${data.affiliate.discount_amount_usd.toFixed(2)} saved)`,
          { duration: 6000 }
        );
      }
      if (data.tax && (data.tax.tax_amount_usd || 0) > 0) {
        toast.info(
          `Charging $${data.tax.total_usd.toFixed(2)} · $${data.tax.subtotal_usd.toFixed(2)} + ${data.tax.tax_label} $${data.tax.tax_amount_usd.toFixed(2)}`,
          { duration: 5000 }
        );
      }

      // Razorpay Checkout flow
      await openCheckout({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.razorpay_order_id,
        name: "LeadIQ Pro",
        description: `${pack.credits.toLocaleString()} credits · $${pack.price_usd}`,
        prefill: {
          name: data.user?.name || "",
          email: data.user?.email || "",
        },
        theme: { color: "#0a0a0a" },
        modal: {
          ondismiss: () => setBusy(null),
        },
        handler: async (resp) => {
          // Verify on backend
          try {
            await http.post("/credits/verify", {
              purchase_id: data.purchase_id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success(`+${pack.credits.toLocaleString()} credits added`);
            await refresh?.();
            onClose();
          } catch (err) {
            toast.error(
              formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed"
            );
          } finally {
            setBusy(null);
          }
        },
        onFailed: (resp) => {
          toast.error(
            "Payment failed: " + (resp?.error?.description || "unknown error")
          );
          setBusy(null);
        },
      });
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Purchase failed"
      );
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-3xl rounded-sm"
        data-testid="credit-packs-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-3xl tracking-tight">
            Top up — buy credits.
          </DialogTitle>
          <p className="text-sm text-ink-500">
            One-time credit packs that work alongside your monthly plan. Use them
            to handle a busy month without upgrading.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-ink-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-2">
            {packs.map((p) => {
              const perLead = (p.price_usd / p.credits).toFixed(3);
              const isBest = p.discount_pct >= 14;
              return (
                <div
                  key={p.id}
                  data-testid={`credit-pack-${p.id}`}
                  className={`relative p-5 border rounded-sm flex flex-col bg-white transition-transform hover:-translate-y-0.5 ${
                    isBest ? "border-ink-900 ring-1 ring-ink-900" : "border-ink-100"
                  }`}
                >
                  {isBest && (
                    <div className="absolute -top-2.5 left-5 inline-flex items-center gap-1 bg-ink-900 text-white text-[9px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm">
                      <Sparkles className="w-2.5 h-2.5" /> Best value
                    </div>
                  )}
                  <div className="flex items-baseline gap-1">
                    <span className="font-display font-black text-3xl tracking-tighter text-ink-900">
                      {p.credits.toLocaleString()}
                    </span>
                    <span className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                      credits
                    </span>
                  </div>
                  <div className="mt-1 text-2xl font-display font-extrabold text-ink-900">
                    ${p.price_usd}
                  </div>
                  <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mt-1">
                    ${perLead} / lead
                  </div>
                  {p.discount_pct > 0 && (
                    <div className="mt-2 inline-flex items-center gap-1 bg-[#E8F5E9] text-[#2E7D32] text-[10px] uppercase tracking-widestx font-bold px-2 py-1 rounded-sm w-fit">
                      <Zap className="w-2.5 h-2.5" />
                      Save {p.discount_pct}%
                    </div>
                  )}

                  <ul className="space-y-1.5 mt-4 mb-5 flex-1">
                    <li className="text-xs flex items-start gap-2 text-ink-700">
                      <Check className="w-3 h-3 mt-0.5 shrink-0 text-[#2E7D32]" />
                      Adds to this month only
                    </li>
                    <li className="text-xs flex items-start gap-2 text-ink-700">
                      <Check className="w-3 h-3 mt-0.5 shrink-0 text-[#2E7D32]" />
                      Stacks with your plan limit
                    </li>
                  </ul>

                  <button
                    data-testid={`credit-pack-buy-${p.id}`}
                    onClick={() => onPurchase(p)}
                    disabled={busy !== null}
                    className={`w-full text-xs font-medium uppercase tracking-widestx py-2.5 rounded-sm transition-colors disabled:opacity-50 ${
                      isBest
                        ? "bg-ink-900 hover:bg-ink-700 text-white"
                        : "bg-ink-100 hover:bg-ink-200 text-ink-900"
                    }`}
                  >
                    {busy === p.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin inline" />
                    ) : (
                      `Buy ${p.credits.toLocaleString()} credits`
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p
          data-testid="credit-packs-demo-note"
          className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 text-center mt-4"
        >
          Secure payment via Razorpay · USD billing
        </p>
      </DialogContent>
    </Dialog>
  );
}
